import React, { useState } from 'react';
import { Target, CalendarClock, ListTodo, BookOpen, NotebookPen, Check, Plus, Repeat, CalendarRange, X } from 'lucide-react';
import {
  GoalRow, NoteChip, ItemChip, HabitTick, StreakBadge, ProgressRing, LinkedTasksBadge,
} from './PlannerBits';
import { SharedHabitCell, SharedBadge } from './GoalShareBits';
import { DOW_HEADERS, dayNum, timeToMin, weekStart, prettyDateLong } from './plannerUtils';
import PlannerMiniCalendar from './PlannerMiniCalendar';
import { RichTaskCard } from './TaskCardKit';

/* Per-day task dialog — opened from the Weekly "Due (n) / Done (n)" chips so a
   whole day's tasks render as full-parity cards in a grid without the Weekly
   matrix ever needing to scroll. Mirrors the Tasks → Today grid. */
function DayTasksDialog({ day, onClose, cardHandlers }) {
  if (!day) return null;
  const due = [], done = [];
  for (const t of (day.tasks || [])) { (t.completed ? done : due).push(t); }
  const prioRank = { high: 0, medium: 1, normal: 2, low: 3 };
  due.sort((a, b) => (prioRank[a.priority] ?? 2) - (prioRank[b.priority] ?? 2));
  done.sort((a, b) => (b.completed_at || '').localeCompare(a.completed_at || ''));
  const dateObj = (() => { try { return new Date(`${day.date}T00:00:00`); } catch { return null; } })();
  const title = dateObj ? prettyDateLong(day.date) : day.date;
  const gridCls = 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3';
  const renderCard = (t) => (
    <RichTaskCard key={t.id} task={t}
      onOpen={cardHandlers.onOpen} onQuickActions={cardHandlers.onQuickActions} onOpenSubtasks={cardHandlers.onOpenSubtasks} />
  );
  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center p-4 pt-[7vh]" data-testid="planner-weekly-day-dialog" onClick={onClose}>
      <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" />
      <div role="dialog" aria-label={`Tasks for ${title}`} onClick={(e) => e.stopPropagation()}
        className="relative flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-[color:var(--p-border)] bg-[color:var(--p-card)] shadow-[var(--p-shadow)]">
        <div className="flex items-center justify-between border-b border-[color:var(--p-line)] px-5 py-3.5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[15px] font-semibold text-[color:var(--p-text)]"><ListTodo size={16} className="text-[color:var(--p-accent)]" /> {title}</div>
            <div className="p-tnum mt-0.5 text-[12px] text-[color:var(--p-muted)]">{due.length} to do &middot; {done.length} done</div>
          </div>
          <button type="button" onClick={onClose} data-testid="planner-weekly-day-dialog-close"
            className="n-focus grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[color:var(--p-muted-2)] transition-colors hover:bg-[color:var(--p-accent-bg)] hover:text-[color:var(--p-text)]"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto n-scroll px-5 py-4">
          {due.length === 0 && done.length === 0 ? (
            <div className="rounded-[var(--p-radius-md)] bg-[color:var(--p-card-2)] px-4 py-10 text-center text-[13px] text-[color:var(--p-muted)]">No tasks for this day.</div>
          ) : (
            <div className="space-y-5">
              {due.length > 0 && (
                <section data-testid="planner-weekly-day-dialog-due">
                  <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--p-accent)]">To do &middot; {due.length}</h4>
                  <div className={gridCls}>{due.map(renderCard)}</div>
                </section>
              )}
              {done.length > 0 && (
                <section data-testid="planner-weekly-day-dialog-done">
                  <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--p-success)]">Completed &middot; {done.length}</h4>
                  <div className={gridCls}>{done.map(renderCard)}</div>
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---- small building blocks ---------------------------------------------- */
const LaneTitle = ({ icon: Icon, children, right }) => (
  <div className="[grid-column:1/-1] flex items-center gap-2 border-y border-[color:var(--p-line)] bg-[color:var(--p-card-2)] px-4 py-2.5">
    <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.13em] text-[color:var(--p-muted-2)]">
      {Icon && <Icon size={13} className="text-[color:var(--p-accent)]" />} {children}
    </span>
    {right && <span className="ml-auto">{right}</span>}
  </div>
);

export default function WeeklyPlannerView({
  data, onToggleGoal, onQuantityGoal, onOpenTask, onOpenNote, onOpenItem, onEditGoal, onNewEvent, onOpenDate, onOpenWeek, onMoveTask, cardHandlers,
}) {
  const days = data.days || [];
  const habitRows = data.habit_rows || [];
  const summary = data.habit_summary || { done: 0, total: 0, pct: 0 };
  const weeklyGoals = data.weekly_goals || [];
  const monthlyGoals = data.monthly_goals || [];
  const todayIdx = days.findIndex((d) => d.is_today);
  const allDaySpans = data.all_day_spans || { spans: [], lane_count: 0 };
  const allDayEvents = data.all_day_events || [];
  const openSpan = (s) => {
    const obj = allDayEvents.find((o) => o.id === s.id && o.start === s.start)
      || allDayEvents.find((o) => o.id === s.id);
    if (obj) onOpenItem(obj);
  };

  // Per-day task dialog (opened from the Due/Done count chips).
  const [dayDialog, setDayDialog] = useState(null);

  const sched = days.map((d) =>
    [...(d.events || []), ...(d.meetings || [])].sort(
      (a, b) => (timeToMin(a.start_time) ?? 1e9) - (timeToMin(b.start_time) ?? 1e9)));

  const cellTone = (idx, extra = '') =>
    `border-b border-r border-[color:var(--p-line)] ${idx === 6 ? 'border-r-0' : ''} ${idx === todayIdx ? 'bg-[color:var(--p-accent-bg)]/55' : ''} ${extra}`;

  const labelCell = (text) => (
    <div className="flex items-center border-b border-r border-[color:var(--p-line)] px-3 py-2 text-[10.5px] font-semibold uppercase tracking-wider text-[color:var(--p-muted-2)]">{text}</div>
  );

  return (
    <div className="p-fade-in mx-auto max-w-[1500px]" data-testid="planner-weekly-view">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
        {/* Sidebar — mini month calendar: click any day to open that week */}
        <aside className="order-first xl:w-[260px] xl:shrink-0">
          <PlannerMiniCalendar mode="week" anchor={data.week_start} today={data.today} onPick={(d) => onOpenWeek && onOpenWeek(weekStart(d))} />
        </aside>

        {/* Main — matrix + classic goals */}
        <div className="min-w-0 flex-1 space-y-4">
          <div className="overflow-x-auto p-noscroll rounded-[var(--p-radius-lg)] border border-[color:var(--p-border)] bg-[color:var(--p-card)] shadow-[var(--p-shadow)]">
        <div className="grid min-w-[940px] grid-cols-[172px_repeat(7,minmax(0,1fr))]">
          {/* Header row */}
          <div className="border-b border-r border-[color:var(--p-line)]" />
          {days.map((d, i) => (
            <button
              key={d.date}
              type="button"
              onClick={() => onOpenDate(d.date)}
              data-testid={d.is_today ? 'planner-weekly-today-header' : 'planner-weekly-day-header'}
              className={`n-focus flex flex-col items-center border-b border-r border-[color:var(--p-line)] py-2.5 transition-colors hover:text-[color:var(--p-accent)] ${i === 6 ? 'border-r-0' : ''} ${i === todayIdx ? 'bg-[color:var(--p-accent-bg)]/55' : ''}`}
            >
              <span className={`text-[10.5px] font-bold uppercase tracking-wider ${i === todayIdx ? 'text-[color:var(--p-accent)]' : 'text-[color:var(--p-muted-2)]'}`}>{DOW_HEADERS[d.weekday]}</span>
              <span className={`p-tnum text-[16px] font-bold ${i === todayIdx ? 'text-[color:var(--p-accent)]' : 'text-[color:var(--p-text)]'}`}>{dayNum(d.date)}</span>
            </button>
          ))}

          {/* GOALS lane */}
          <LaneTitle
            icon={Target}
            right={habitRows.length > 0 ? (
              <span className="flex items-center gap-2.5" data-testid="planner-habit-summary">
                <ProgressRing pct={summary.pct} size={34} thickness={5}>
                  <span className="p-tnum text-[9px] font-bold text-[color:var(--p-accent)]">{summary.pct}%</span>
                </ProgressRing>
                <span className="p-tnum text-[11px] font-semibold text-[color:var(--p-text-soft)]">{summary.done}/{summary.total} ticks</span>
              </span>
            ) : null}
          >Habits</LaneTitle>

          {habitRows.length === 0 ? (
            <div className="[grid-column:1/-1] px-4 py-6 text-center text-[12.5px] text-[color:var(--p-muted)]" data-testid="planner-weekly-habits-empty">No habits active this week. Create a daily goal or a counted weekly goal.</div>
          ) : habitRows.map((row) => (
            <React.Fragment key={row.id}>
              <button
                type="button"
                onClick={() => onEditGoal(row)}
                data-testid="planner-habit-row-name"
                className="n-focus flex flex-col justify-center gap-1 border-b border-r border-[color:var(--p-line)] px-3 py-2 text-left transition-colors hover:bg-[color:var(--p-accent-bg)]/40"
              >
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-[13px] font-semibold text-[color:var(--p-text)]">{row.title}</span>
                  {row.is_shared && <SharedBadge count={(row.shared_with || []).length + 1} />}
                  <StreakBadge count={row.streak} />
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-[color:var(--p-muted-2)]">
                  {row.is_counted ? (
                    <>
                      <span className="p-tnum font-semibold text-[color:var(--p-text-soft)]">{row.period_count}/{row.target_count}</span>
                      <span>/ week</span>
                      {row.period_met && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-[color:var(--p-accent-bg)] px-1.5 text-[9.5px] font-bold text-[color:var(--p-accent)]"><Check size={9} strokeWidth={3.5} /> Met</span>
                      )}
                    </>
                  ) : (
                    <span className="p-tnum font-semibold text-[color:var(--p-text-soft)]">{row.done}/{row.total} days</span>
                  )}
                </div>
              </button>
              {row.cells.map((cell, i) => (
                <div key={cell.date} className={`flex items-center justify-center py-2 ${cellTone(i)}`}>
                  {row.is_shared ? (
                    <SharedHabitCell cell={cell} size={27} onToggle={(date, note) => onToggleGoal({ id: row.id, period_key: date, is_shared: true }, note)} />
                  ) : (
                    <HabitTick cell={cell} size={27} onToggle={(date) => onToggleGoal({ id: row.id, period_key: date })} />
                  )}
                </div>
              ))}
            </React.Fragment>
          ))}

          {/* SCHEDULE lane */}
          <LaneTitle icon={CalendarClock} right={<span className="text-[10.5px] text-[color:var(--p-muted-2)]">events &amp; meetings</span>}>Schedule</LaneTitle>

          {/* All-day / multi-day band — bars span the covered day columns */}
          {allDaySpans.spans.length > 0 && (
            <React.Fragment>
              <div className="flex items-center gap-1.5 border-b border-r border-[color:var(--p-line)] px-3 py-2 text-[10.5px] font-semibold uppercase tracking-wider text-[color:var(--p-muted-2)]">
                <CalendarRange size={12} className="text-[color:var(--p-accent)]" /> All day
              </div>
              <div className="[grid-column:2/-1] border-b border-[color:var(--p-line)] p-1.5" data-testid="planner-weekly-allday-band">
                <div className="grid grid-cols-7 gap-y-1" style={{ gridTemplateRows: `repeat(${allDaySpans.lane_count || 1}, minmax(22px, auto))` }}>
                  {allDaySpans.spans.map((s) => (
                    <button
                      key={`${s.id}-${s.start}`}
                      type="button"
                      onClick={() => openSpan(s)}
                      data-testid="planner-weekly-allday-bar"
                      title={s.title}
                      style={{ gridColumn: `${s.col_start + 1} / ${s.col_end + 2}`, gridRow: s.lane + 1 }}
                      className={`n-focus flex items-center gap-1 overflow-hidden border border-[color:var(--p-accent-soft)] bg-[color:var(--p-accent-bg)] px-2 py-1 text-left text-[11.5px] font-semibold text-[color:var(--p-accent)] transition-colors hover:bg-[color:var(--p-accent)] hover:text-white ${s.continues_before ? 'rounded-l-none border-l-0' : 'rounded-l-md'} ${s.continues_after ? 'rounded-r-none border-r-0' : 'rounded-r-md'}`}
                    >
                      {s.continues_before && <span aria-hidden="true" className="shrink-0">&lsaquo;</span>}
                      {s.recurring && <Repeat size={10} className="shrink-0" />}
                      <span className="min-w-0 flex-1 truncate">{s.title}</span>
                      {s.task_total > 0 && <LinkedTasksBadge total={s.task_total} done={s.task_done} compact />}
                      {s.continues_after && <span aria-hidden="true" className="shrink-0">&rsaquo;</span>}
                    </button>
                  ))}
                </div>
              </div>
            </React.Fragment>
          )}

          {labelCell('When')}
          {days.map((d, i) => (
            <div key={d.date} className={cellTone(i, 'space-y-1.5 p-2 min-h-[58px]')} data-testid="planner-weekly-schedule-cell">
              {sched[i].length === 0
                ? <button type="button" onClick={() => onNewEvent(d.date)} aria-label="Add event" className="n-focus grid h-7 w-7 place-items-center rounded-md text-[color:var(--p-muted-2)] opacity-0 transition-opacity hover:bg-[color:var(--p-accent-bg)] hover:text-[color:var(--p-accent)] group-hover:opacity-100"><Plus size={14} /></button>
                : sched[i].map((it) => <ItemChip key={it.id} item={it} onOpen={onOpenItem} />)}
            </div>
          ))}

          {/* TASKS lane — compact Due/Done count chips per day. Tap a count to
              open that day's tasks as full-parity cards in a dialog (keeps the
              Weekly matrix scroll-free). */}
          <LaneTitle icon={ListTodo} right={<span className="text-[10.5px] text-[color:var(--p-muted-2)]">from Tasks &middot; tap a count to open</span>}>Tasks</LaneTitle>
          {labelCell('To do')}
          {days.map((d, i) => {
            const dayTasks = d.tasks || [];
            const dueN = dayTasks.filter((t) => !t.completed).length;
            const doneN = dayTasks.filter((t) => t.completed).length;
            const hasAny = (dueN + doneN) > 0;
            return (
              <div
                key={d.date}
                data-testid="planner-weekly-tasks-cell"
                className={cellTone(i, 'flex flex-wrap content-start gap-1.5 p-2 min-h-[58px]')}
              >
                {dueN > 0 && (
                  <button type="button" onClick={() => setDayDialog(d)} data-testid="planner-weekly-due-chip"
                    title={`${dueN} to do — open`}
                    className="n-focus inline-flex items-center gap-1 rounded-full border border-[color:var(--p-border)] bg-[color:var(--p-card-2)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--p-accent)] transition-colors hover:border-[color:var(--p-accent)] hover:bg-[color:var(--p-accent-bg)]">
                    Due <span className="p-tnum rounded-full bg-[color:var(--p-accent)] px-1.5 text-[10px] font-bold text-white">{dueN}</span>
                  </button>
                )}
                {doneN > 0 && (
                  <button type="button" onClick={() => setDayDialog(d)} data-testid="planner-weekly-done-chip"
                    title={`${doneN} done — open`}
                    className="n-focus inline-flex items-center gap-1 rounded-full border border-[color:var(--p-border)] bg-[color:var(--p-card-2)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--p-success)] transition-colors hover:border-[color:var(--p-success)]">
                    <Check size={11} strokeWidth={3} /><span className="p-tnum">{doneN}</span>
                  </button>
                )}
                {!hasAny && <span className="self-center text-[11px] italic text-[color:var(--p-muted-2)] opacity-60">—</span>}
              </div>
            );
          })}

          {/* NOTES ACTIVITY lane */}
          <LaneTitle icon={BookOpen} right={<span className="text-[10.5px] text-[color:var(--p-muted-2)]">created &middot; updated &middot; shared</span>}>Notes activity</LaneTitle>
          {labelCell('Activity')}
          {days.map((d, i) => (
            <div key={d.date} className={cellTone(i, 'space-y-1 p-2 min-h-[44px]')} data-testid="planner-weekly-notes-cell">
              {(d.notes || []).map((n, idx) => <NoteChip key={`${n.note_id}-${idx}`} note={n} onOpen={onOpenNote} />)}
            </div>
          ))}

          {/* DAY NOTE lane */}
          <LaneTitle icon={NotebookPen} right={<span className="text-[10.5px] text-[color:var(--p-muted-2)]">your note for the day</span>}>Day note</LaneTitle>
          {labelCell('Note')}
          {days.map((d, i) => (
            <button
              key={d.date}
              type="button"
              onClick={() => onOpenDate(d.date)}
              data-testid="planner-weekly-day-note-cell"
              title={d.day_note ? 'Open day to edit' : 'Add a day note'}
              className={`n-focus ${cellTone(i, 'p-2 min-h-[52px] text-left align-top transition-colors hover:bg-[color:var(--p-accent-bg)]/40')}`}
            >
              {d.day_note
                ? <span className="line-clamp-3 whitespace-pre-wrap text-[11.5px] leading-snug text-[color:var(--p-text-soft)]">{d.day_note}</span>
                : <span className="text-[11px] italic text-[color:var(--p-muted-2)] opacity-70">Add a note…</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Classic period goals (single-tick) below the matrix */}
      {(weeklyGoals.length > 0 || monthlyGoals.length > 0) && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {weeklyGoals.length > 0 && (
            <div className="rounded-[var(--p-radius-lg)] border border-[color:var(--p-border)] bg-[color:var(--p-card)] p-4 shadow-[var(--p-shadow)]" data-testid="planner-weekly-goals-panel">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--p-muted-2)]">This week&rsquo;s goals</div>
              <div className="space-y-0.5">{weeklyGoals.map((g) => <GoalRow key={g.id} goal={g} onToggle={onToggleGoal} onQuantity={onQuantityGoal} onEdit={onEditGoal} compact />)}</div>
            </div>
          )}
          {monthlyGoals.length > 0 && (
            <div className="rounded-[var(--p-radius-lg)] border border-[color:var(--p-border)] bg-[color:var(--p-card)] p-4 shadow-[var(--p-shadow)]" data-testid="planner-weekly-monthly-goals-panel">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--p-muted-2)]">This month&rsquo;s goals</div>
              <div className="space-y-0.5">{monthlyGoals.map((g) => <GoalRow key={g.id} goal={g} onToggle={onToggleGoal} onQuantity={onQuantityGoal} onEdit={onEditGoal} compact />)}</div>
            </div>
          )}
        </div>
      )}
        </div>
      </div>

      <DayTasksDialog day={dayDialog} onClose={() => setDayDialog(null)} cardHandlers={cardHandlers} />

    </div>
  );
}
